import { useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { MAINTENANCE_PROJECTS_KEY, MAINTENANCE_STATS_KEY } from '@/lib/queryKeys/maintenanceKeys';

export type BaselineField =
  | 'handover_date'
  | 'warranty_period'
  | 'gross_floor_area_m2'
  | 'total_manhour_estimate';

export type BaselineValue = number | Date | null;

interface MutateArgs {
  projectId: string;
  field: BaselineField;
  value: BaselineValue;
}

const toDbValue = (field: BaselineField, value: BaselineValue) => {
  if (value === null) return null;
  switch (field) {
    case 'handover_date':
      return value instanceof Date ? format(value, 'yyyy-MM-dd') : null;
    case 'warranty_period':
      return `${value as number} months`;
    case 'gross_floor_area_m2':
    case 'total_manhour_estimate':
      return value as number;
  }
};

const isRlsError = (err: any): boolean => {
  const msg = (err?.message || '').toLowerCase();
  return err?.code === '42501' || msg.includes('row-level security') || msg.includes('permission denied');
};

export const useUpdateProjectBaseline = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ projectId, field, value }: MutateArgs) => {
      const dbValue = toDbValue(field, value);
      const { error } = await supabase
        .from('projects')
        .update({ [field]: dbValue } as any)
        .eq('id', projectId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [MAINTENANCE_PROJECTS_KEY] });
      queryClient.invalidateQueries({ queryKey: [MAINTENANCE_STATS_KEY] });
      queryClient.invalidateQueries({ queryKey: ['projects-optimized-v2'] });
      queryClient.invalidateQueries({ queryKey: ['project-detail'], exact: false, refetchType: 'active' });
      queryClient.invalidateQueries({ queryKey: ['projects', 'status-stats'], exact: false, refetchType: 'active' });
      toast.success('Đã cập nhật');
    },
    onError: (err: any) => {
      if (isRlsError(err)) {
        toast.error('Bạn không có quyền chỉnh sửa');
      } else {
        toast.error(err?.message || 'Cập nhật thất bại');
      }
    },
  });
};
